import { z } from 'zod';

import {
  CustomerUploadContentType,
  CustomizationInputType,
} from '@nucleus-commerce/api-client/admin';

export const UPLOAD_CONTENT_TYPES = Object.values(CustomerUploadContentType);
export const IMAGE_CONTENT_TYPES = UPLOAD_CONTENT_TYPES.filter((type) => type.startsWith('image/'));

const KEY = /^[a-z][a-z0-9_]*$/;
const VALUE_CODE = /^[a-z0-9][a-z0-9_.-]*$/;

const wholeAtLeast = (min: number, message: string) =>
  z
    .string()
    .trim()
    .refine((v) => /^\d+$/.test(v) && Number(v) >= min, { error: message });

/**
 * One inline option. The stored `value` is what carts and orders reference, so an option that
 * already exists on the server keeps it; only its label changes.
 */
export const customizationOptionRowSchema = z.object({
  value: z
    .string()
    .trim()
    .min(1, { error: 'dictionary:validation.valueRequired' })
    .max(120, { error: 'dictionary:validation.labelLength' })
    .regex(VALUE_CODE, { error: 'dictionary:validation.valueFormat' }),
  label: z
    .string()
    .trim()
    .min(1, { error: 'dictionary:validation.labelRequired' })
    .max(120, { error: 'dictionary:validation.labelLength' }),
  /** Added on top of the customization's own price when this option is picked; null adds nothing. */
  priceDelta: z
    .object({ amount: z.number().int(), currency: z.string() })
    .nullable()
    .refine((v) => v === null || v.amount >= 0, { error: 'dictionary:validation.priceDelta' }),
  existing: z.boolean(),
});

export const OPTIONS_SOURCES = ['inline', 'size_set'] as const;
export type OptionsSource = (typeof OPTIONS_SOURCES)[number];

/**
 * One customization as the dialog edits it. Type-specific fields (options, size set, maxLength,
 * pattern, accept) stay in the form whatever the type; the mapper sends only the ones the type
 * takes, and the backend's "size set or inline options, never both" rule is checked here first.
 */
export const customizationFormSchema = z
  .object({
    key: z
      .string()
      .trim()
      .min(1, { error: 'dictionary:validation.keyRequired' })
      .max(64, { error: 'dictionary:validation.keyLength' })
      .regex(KEY, { error: 'dictionary:validation.keyFormat' }),
    label: z
      .string()
      .trim()
      .min(1, { error: 'dictionary:validation.labelRequired' })
      .max(120, { error: 'dictionary:validation.labelLength' }),
    inputType: z.enum(CustomizationInputType),
    isRequired: z.boolean(),
    priceDelta: z
      .object({ amount: z.number().int(), currency: z.string() })
      .nullable()
      .refine((v) => v === null || v.amount >= 0, { error: 'dictionary:validation.priceDelta' }),
    affectsLeadTimeDays: wholeAtLeast(0, 'dictionary:validation.leadTime'),
    helpText: z.string().trim().max(500, { error: 'dictionary:validation.helpTextLength' }),
    optionsSource: z.enum(OPTIONS_SOURCES),
    /** Empty string means no size set. */
    sizeSetId: z.string(),
    options: z.array(customizationOptionRowSchema).max(200, {
      error: 'dictionary:validation.optionsMax',
    }),
    maxLength: z.string().trim(),
    pattern: z.string().trim().max(200, { error: 'dictionary:validation.patternLength' }),
    /** File inputs: true sends `accept: null`, every type the platform allows. */
    acceptAny: z.boolean(),
    accept: z.array(z.enum(CustomerUploadContentType)),
  })
  .superRefine((values, ctx) => {
    if (values.inputType === 'text' && values.maxLength !== '') {
      const n = Number(values.maxLength);
      if (!/^\d+$/.test(values.maxLength) || n < 1 || n > 2000) {
        ctx.addIssue({
          code: 'custom',
          path: ['maxLength'],
          message: 'dictionary:validation.maxLength',
        });
      }
    }
    if (values.inputType === 'file' && !values.acceptAny && values.accept.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['accept'],
        message: 'dictionary:validation.acceptRequired',
      });
    }
    if (values.inputType === 'select') {
      if (values.optionsSource === 'size_set' && values.sizeSetId === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['sizeSetId'],
          message: 'dictionary:validation.sizeSetRequired',
        });
      }
      if (values.optionsSource === 'inline') {
        if (values.options.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['options'],
            message: 'dictionary:validation.optionsRequired',
          });
        }
        const seen = new Set<string>();
        values.options.forEach((row, index) => {
          const value = row.value.trim();
          if (value && seen.has(value)) {
            ctx.addIssue({
              code: 'custom',
              path: ['options', index, 'value'],
              message: 'dictionary:validation.duplicateValue',
            });
          }
          seen.add(value);
        });
      }
    }
  });

export type CustomizationFormInput = z.input<typeof customizationFormSchema>;
export type CustomizationFormValues = z.output<typeof customizationFormSchema>;
export type CustomizationOptionRow = z.input<typeof customizationOptionRowSchema>;
